import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { MoreInfoButton } from '../MoreInfoButton';

import { ParticipateButton } from './ParticipateButton';

import icGold1 from '@/assets/ic_gold_1.svg';
import icGold2 from '@/assets/ic_gold_2.svg';
import icGold3 from '@/assets/ic_gold_3.svg';
import { Button } from '@/components';
import { MainBox } from '@/components/GenericsTournaments';
import ROUTES from '@/router/routes';
import { ResponseInfoFeoroUsuario } from '@/types/goldDate';
import { EnumContextGDT } from '@/types/team';
import {
  toLowerCase,
  extractNumberWithHash,
  formatNumberToLocale,
} from '@/utils';

type DesktopContentProps = {
  dataInitUsuarioOro?: ResponseInfoFeoroUsuario;
  onCardButtonClick: () => void;
};

const DesktopContent = ({
  dataInitUsuarioOro: data,
  onCardButtonClick,
}: DesktopContentProps) => {
  const navigate = useNavigate();

  const isEndedAndNoParticipate = !data?.participaActual && data?.estaPublicado;

  const cardHeader = () => {
    if ((data && data.estaInactiva) || isEndedAndNoParticipate) {
      return (
        <h2 className="text-base font-bold text-gdt-blue pt-8 text-left pl-16">
          {isEndedAndNoParticipate ? (
            <p className="text-black mt-2 mb-6">
              Fecha de Oro ya finalizó.
              <br />
              ¡Te esperamos en la próxima edición!
            </p>
          ) : (
            data.msjInactiva
          )}
        </h2>
      );
    }

    if (!data?.ta && data?.primerFecha) {
      return (
        <div className="flex flex-col gap-2">
          <h2 className="text-base font-bold text-gdt-blue pt-8 text-left pl-16">
            ¡Muy pronto podrás participar!
          </h2>
          <strong className="text-left pl-16">{`La primera Fecha de Oro se juega en la ${toLowerCase(
            data?.primerFecha
          )}.`}</strong>
        </div>
      );
    }

    const cantPart = data?.ta?.cantPart ?? 0;

    return (
      <div className="flex flex-col text-left pl-16 gap-2 pt-8">
        <div className="flex gap-3 items-center">
          <p className="text-base font-bold">{data?.ta?.nombreFecha}</p>
          <p className="text-gdt-gray-4 font-semibold text-xs">
            {cantPart < 20
              ? `${20 - cantPart} cupos disponibles`
              : 'Sin cupos disponibles'}
          </p>
        </div>
        {/* TODO: puede ir */}
        {/* {!noParticipateAndInactive && (
          <p className="text-2xl text-gdt-status-yellow">{`Fecha de Oro ${extractNumberWithHash(
            data?.ta?.nombre
          )}`}</p>
        )} */}

        <p className="text-2xl text-gdt-status-yellow">{`Fecha de Oro ${extractNumberWithHash(
          data?.ta?.nombre
        )}`}</p>
      </div>
    );
  };

  const priceAndPinInfo = () => {
    return (
      <div className="flex flex-col text-left pl-16 pt-8 gap-2">
        {data?.inscripcionPosible && (
          <p className="text-base font-bold">{`Participás por $${formatNumberToLocale(
            data?.premio ?? 0
          )}`}</p>
        )}
        <p className="text-gdt-gray-4 font-semibold text-xs">
          {`Pin especial $${formatNumberToLocale(data?.precioDeListaPin ?? 0)}`}
        </p>
      </div>
    );
  };

  const actionShowTourney = useMemo(() => {
    return (
      (data &&
        data?.actualesSinPublicar &&
        data?.actualesSinPublicar.length > 0) ||
      (data && data?.todosLosPublicados && data?.todosLosPublicados?.length > 0)
    );
  }, [data]);

  const ActionShowTourney = useCallback(() => {
    return (
      <Button
        className="text-gdt-blue font-bold"
        type="button"
        variant="text"
        // muestro el listado de participando -presente o pasado
        onClick={() => navigate(ROUTES.goldDate.list, { state: true })}
      >
        Ver mis torneos Fecha de Oro
      </Button>
    );
  }, [navigate]);

  return (
    <MainBox
      className="shadow-xl w-[890px] mx-auto"
      context={EnumContextGDT.GoldDate}
    >
      <div className="flex flex-1 flex-col pb-5">
        {cardHeader()}
        <div className="w-full text-gdt-gray font-medium text-xs text-left relative">
          <ul className="pl-16 space-y-5 pt-6">
            <li className="pl-[35px]">
              <strong className="text-base">¿Cómo participar?</strong>
            </li>

            <li className="flex items-center gap-3">
              <figure className="flex-shrink-0">
                <img
                  className="object-contain"
                  height={25}
                  src={icGold1}
                  width={25}
                />
              </figure>
              <p className="leading-snug text-sm">
                Anotate ingresando el Pin Especial de la Fecha de Oro.
              </p>
            </li>

            <li className="flex items-center gap-3">
              <figure className="flex-shrink-0">
                <img
                  className="object-contain"
                  height={25}
                  src={icGold2}
                  width={25}
                />
              </figure>
              <p className="leading-snug text-sm">
                Competí con tu Equipo de Primera.
              </p>
            </li>

            <li className="flex items-center gap-3">
              <figure className="flex-shrink-0">
                <img
                  className="object-contain"
                  height={25}
                  src={icGold3}
                  width={25}
                />
              </figure>
              {data?.estaInactiva ? (
                <p className="leading-snug text-sm">
                  {`Competí en fechas especiales por $${formatNumberToLocale(
                    data?.premio ?? 0
                  )}.`}
                </p>
              ) : (
                <p className="leading-snug text-sm">
                  {`Competí en la ${toLowerCase(
                    data?.ta ? data?.ta?.nombreFecha : data?.primerFecha
                  )} por los $${formatNumberToLocale(data?.premio ?? 0)}.`}
                </p>
              )}
            </li>
          </ul>
        </div>
        {!isEndedAndNoParticipate &&
          data &&
          data?.ta &&
          !data?.estaInactiva &&
          priceAndPinInfo()}
        <div className="flex pl-16 pt-8 gap-6 items-center content-center">
          {data &&
            data?.ta &&
            !isEndedAndNoParticipate &&
            !data.estaInactiva && (
              <ParticipateButton
                dataInitUsuarioOro={data}
                onClick={() => onCardButtonClick()}
              />
            )}

          {/* BOTON MAS INFORMACION */}
          <MoreInfoButton />

          {((actionShowTourney && !data?.ta) ||
            (actionShowTourney && data?.estaInactiva) ||
            (actionShowTourney && isEndedAndNoParticipate)) && (
            <ActionShowTourney />
          )}
        </div>
        <div className="flex justify-center pt-4">
          {actionShowTourney &&
            data &&
            data?.ta &&
            !isEndedAndNoParticipate &&
            !data.estaInactiva && <ActionShowTourney />}
        </div>
      </div>
    </MainBox>
  );
};

export default DesktopContent;
